import { IBusinessRule, IBusinessRuleItemSpecifier } from '../types/brandMenu';
import {
  BusinessRuleCategory,
  BusinessRuleOption,
} from '../constants/brandMenu';
import logger from './logger';
import { CartItem, CartModifierGroup } from './cart';
import { UnavailableItemsByName } from '../redux/features/menuUnification/menuUnification.props';
import { isBMModifierAnUnavailableConstraint } from './menuUnification.utils';
import { getParentModifier } from './menu';
import { hasInvalidSelectionForTarget } from './optionReplacement.utils';
import { ALL_ENTRIES } from '../constants';
import { getOptionModifierGroups } from './lazyOptionBuild';

export interface OptionReplacementInfo {
  shouldApplyReplacement: boolean;
  targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }>;
}

/**
 * Extracts the base size from a combo size option (e.g., "small combo" -> "small")
 */
export const extractBaseSize = (sizeOption: string): string | null => {
  const suffixes = [' combo', ' side', ' drink', ' size'];
  for (const suffix of suffixes) {
    if (sizeOption.toLowerCase().endsWith(suffix)) {
      return sizeOption.slice(0, -suffix.length).trim();
    }
  }
  return null;
};

const normalizeName = (value: string): string => value.toLowerCase().trim();

type NestedModifierGroupEntry = {
  modifierGroup: CartItem['modifierGroups'][string];
  ownerNode: CartItem;
};

/**
 * Collects modifier groups reachable through already-selected items:
 * childModifierGroups → selectedItems → modifierGroups (recursive).
 * Only walks the selection tree — cheap because it only visits items the user
 * has already chosen.
 */
export const collectNestedModifierGroups = (
  cartItem: CartItem,
  result: Map<string, NestedModifierGroupEntry> = new Map()
): Map<string, NestedModifierGroupEntry> => {
  for (const childGroup of Object.values(cartItem.childModifierGroups || {})) {
    for (const selectedItem of Object.values(childGroup.selectedItems || {})) {
      for (const group of Object.values(selectedItem.modifierGroups || {})) {
        if (group.name && !result.has(group.name)) {
          result.set(group.name, {
            modifierGroup: group,
            ownerNode: selectedItem,
          });
        }
      }
      collectNestedModifierGroups(selectedItem, result);
    }
  }
  return result;
};

/**
 * Finds the menu item definition for the just-selected item by searching
 * the modifier group where the selection happened. Returns the item's own
 * nested modifier groups keyed by name — only the groups exposed by this
 * specific item, not the whole tree.
 */
const getSelectedItemNestedGroups = (
  cartItem: CartItem,
  modifierGroupName: string,
  selectedMenuItemName: string
): Map<string, NestedModifierGroupEntry> | null => {
  const result = new Map<string, NestedModifierGroupEntry>();

  for (const modGroup of Object.values(cartItem.modifierGroups || {})) {
    if (modGroup.name !== modifierGroupName) {
      continue;
    }

    const menuItem = Object.values(modGroup.menuItems || {}).find(
      (item) => normalizeName(item.name) === normalizeName(selectedMenuItemName)
    );

    if (!menuItem) {
      continue;
    }

    for (const group of Object.values(
      getOptionModifierGroups(menuItem) || {}
    )) {
      if (group.name) {
        result.set(group.name, {
          modifierGroup: group,
          ownerNode: menuItem as unknown as CartItem,
        });
      }
    }

    return result.size > 0 ? result : null;
  }

  return null;
};

/**
 * Recursively searches through nested childModifierGroups to find selected
 * items matching a set of combo size option names.
 */
const findSelectedComboSizeInNestedGroups = (
  cartItem: CartItem,
  comboSizeOptions: Set<string>
): string | undefined => {
  for (const childGroup of Object.values(cartItem.childModifierGroups || {})) {
    for (const selectedItem of Object.values(childGroup.selectedItems || {})) {
      const normalizedName = normalizeName(selectedItem.name);
      if (comboSizeOptions.has(normalizedName)) {
        return selectedItem.name;
      }

      const found = findSelectedComboSizeInNestedGroups(
        selectedItem,
        comboSizeOptions
      );
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

/**
 * Generic function to match rule patterns (items or constraints) against candidates.
 * Supports wildcard matching with '*' pattern.
 * @param normalizeFn - Function to normalize strings for comparison
 */
export const matchesRulePattern = (
  patterns: Array<string | { name?: string | null } | null | undefined>,
  candidates: Array<string | null | undefined>,
  emptyPatternBehavior: 'allow' | 'deny',
  normalizeFn: (value: string) => string = normalizeName
): boolean => {
  if (!patterns || patterns.length === 0) {
    return emptyPatternBehavior === 'allow';
  }

  const normalizedCandidates = candidates
    .filter((candidate): candidate is string => !!candidate)
    .map((candidate) => normalizeFn(candidate));

  // Handle case where candidate is null/undefined - only match wildcard
  if (normalizedCandidates.length === 0) {
    return patterns.some((pattern) => {
      if (!pattern) {
        return false;
      }
      const patternValue = typeof pattern === 'string' ? pattern : pattern.name;
      return patternValue === '*';
    });
  }

  return patterns.some((pattern) => {
    if (!pattern) {
      return false;
    }

    const patternValue = typeof pattern === 'string' ? pattern : pattern.name;

    if (!patternValue) {
      return false;
    }

    if (patternValue === '*') {
      return true;
    }

    return normalizedCandidates.includes(normalizeFn(patternValue));
  });
};

const matchesRuleItems = (
  items: IBusinessRule['items_to_apply'],
  candidates: Array<string | null | undefined>
): boolean => {
  return matchesRulePattern(items, candidates, 'deny');
};

const hasWildcardItem = (items: IBusinessRule['items_to_apply']): boolean => {
  if (!items || items.length === 0) {
    return false;
  }

  return items.some(({ name }) => name === '*');
};

const matchesRuleConstraints = (
  constraints: IBusinessRule['constraints_to_apply'],
  candidate: string | null
): boolean => {
  return matchesRulePattern(constraints, [candidate], 'allow');
};

const buildComboSizeOptionsSet = (
  comboSizeTransformationRules: IBusinessRule[]
): Set<string> => {
  const options = new Set<string>();
  comboSizeTransformationRules.forEach((rule) => {
    rule.if.options.forEach((option) => {
      if (option !== '*') {
        options.add(normalizeName(option));
      }
    });
  });
  return options;
};

/**
 * Finds the selected combo size option name by matching against if.options from combo size transformation rules
 * Instead of using hardcoded COMBO_SIZE_MODIFIER_NAME_PREFIX, we use the rule's if.options to find matches
 * @param comboSizeOptions - Pre-built set of normalized combo size option names
 * @returns The name of the selected combo size option, or undefined if not found
 */
const getSelectedComboSize = (
  cartItem: CartItem,
  comboSizeOptions: Set<string>
): string | undefined => {
  if (comboSizeOptions.size === 0) {
    return undefined;
  }

  // Search recursively to handle nested modifier group structures
  return findSelectedComboSizeInNestedGroups(cartItem, comboSizeOptions);
};

/**
 * Finds a matching default size selection business rule
 * Matches rules based on if.options instead of constraints_to_apply
 */
const findMatchingDefaultSizeRule = (
  itemName: string,
  selectedOption: string,
  defaultSizeRules: IBusinessRule[]
): IBusinessRule | null => {
  if (!defaultSizeRules || defaultSizeRules.length === 0) {
    return null;
  }

  return (
    defaultSizeRules.find((rule) => {
      // Only match default size selection rules
      if (
        rule.items_to_apply.length > 0 &&
        !matchesRuleItems(rule.items_to_apply, [itemName, selectedOption])
      ) {
        return false;
      }

      // Match rules based on if.options instead of constraints_to_apply
      // Check if the selected option matches any of the rule's if.options
      // "*" means match any option
      const normalizedSelectedOption = normalizeName(selectedOption);
      return rule.if.options.some(
        (option) =>
          option === '*' || normalizeName(option) === normalizedSelectedOption
      );
    }) || null
  );
};

/**
 * Finds all matching combo size transformation business rules
 * Matches rules based on if.options instead of constraints_to_apply
 * @param comboSizeTransformationRules - Pre-filtered combo size transformation rules
 * @returns Array of all matching rules
 */
const findMatchingComboSizeTransformationRules = (
  itemName: string,
  selectedOption: string,
  comboSizeTransformationRules: IBusinessRule[]
): IBusinessRule[] => {
  if (
    !comboSizeTransformationRules ||
    comboSizeTransformationRules.length === 0
  ) {
    return [];
  }

  return comboSizeTransformationRules.filter((rule) => {
    if (
      rule.items_to_apply.length > 0 &&
      !matchesRuleItems(rule.items_to_apply, [itemName])
    ) {
      return false;
    }

    // Match rules based on if.options instead of constraints_to_apply
    // Check if the selected option matches any of the rule's if.options
    // "*" means match any option
    const normalizedSelectedOption = normalizeName(selectedOption);
    return rule.if.options.some(
      (option) =>
        option === '*' || normalizeName(option) === normalizedSelectedOption
    );
  });
};

/**
 * Returns the first default-size rule that applies to a target modifier group,
 * taking into account the current selection context and child item state.
 */
const findDefaultRuleForTarget = (
  defaultSizeRules: IBusinessRule[],
  {
    targetName,
    hasExistingSelection,
    normalizedSelectedOptionName,
    selectedChildItems,
    contextModifierGroupName,
  }: {
    targetName: string;
    hasExistingSelection: boolean;
    normalizedSelectedOptionName: string | null;
    selectedChildItems: Array<{ name: string; modifierGroupName: string }>;
    contextModifierGroupName?: string | null;
  }
): IBusinessRule | undefined => {
  return defaultSizeRules.find((rule) => {
    if (!rule.then.target.includes(targetName)) {
      return false;
    }

    const ruleItems = rule.items_to_apply;
    const wildcardItem = hasWildcardItem(ruleItems);

    if (wildcardItem) {
      return !normalizedSelectedOptionName && !hasExistingSelection;
    }

    if (!ruleItems || ruleItems.length === 0) {
      return false;
    }

    const normalizedRuleItems = ruleItems
      .filter(
        (item): item is IBusinessRuleItemSpecifier =>
          !!item?.name && item.name !== '*'
      )
      .map((item) => normalizeName(item.name));

    // First, try to match based on what the user JUST selected (selectedContext)
    if (normalizedSelectedOptionName) {
      const matchesSelectedOption = normalizedRuleItems.includes(
        normalizedSelectedOptionName
      );

      if (matchesSelectedOption) {
        const matchesIfOption = rule.if.options.some((option: string) => {
          if (option === '*') {
            return true;
          }
          return normalizeName(option) === normalizedSelectedOptionName;
        });

        if (matchesIfOption) {
          return true;
        }
      }
      // Fall through to evaluate selected child items if the chosen option
      // does not satisfy the rule's conditions.
    }

    /**
     * If the just-selected option doesn't match, check ALL selected items across the cart.
     * This allows rules to match based on OTHER modifier groups' selections.
     *
     * Example rule: "If user selected 'burger' in Main Item, then default Drinks to 'medium'"
     * - selectedContext might be "Drinks" → "small drink" (doesn't match rule)
     * - But selectedChildItems contains { name: "burger", modifierGroupName: "Main Item" }
     * - This matches the rule's items_to_apply → rule applies!
     *
     * We exclude items from the context modifier group to avoid matching the trigger itself.
     */
    return selectedChildItems.some((childItem) => {
      // Don't match items from the context group (the one that triggered this evaluation)
      if (
        contextModifierGroupName &&
        normalizeName(childItem.modifierGroupName) ===
          normalizeName(contextModifierGroupName)
      ) {
        return false;
      }

      const normalizedChildName = normalizeName(childItem.name);
      return normalizedRuleItems.includes(normalizedChildName);
    });
  });
};

type ComboTargetState = {
  modifierGroup: CartItem['modifierGroups'][string];
  selectedItems: CartItem[];
  hasExistingSelection: boolean;
};

type DefaultSizeEvaluation = {
  defaultRule: IBusinessRule | undefined;
  enforceDefaultOptions: string[];
};

/**
 * Constructs the current combo target state, including the modifier definition
 * and any existing selections, if the modifier group is present on the cart item.
 */
const buildComboTargetState = (
  cartItem: CartItem,
  targetName: string,
  nestedGroups: Map<string, NestedModifierGroupEntry> = new Map()
): ComboTargetState | null => {
  // First check top-level modifier groups
  const topLevelGroup = Object.values(cartItem.modifierGroups || {}).find(
    (group) => group.name === targetName
  );

  if (topLevelGroup) {
    const cartModifierGroup = topLevelGroup.id
      ? cartItem.childModifierGroups?.[topLevelGroup.id]
      : undefined;
    const selectedItems = Object.values(cartModifierGroup?.selectedItems || {});

    return {
      modifierGroup: topLevelGroup,
      selectedItems,
      hasExistingSelection: selectedItems.length > 0,
    };
  }

  // Look up from pre-collected nested modifier groups (single walk, O(1) lookup)
  const nested = nestedGroups.get(targetName);

  if (!nested) {
    return null;
  }

  const { modifierGroup, ownerNode } = nested;
  const cartModifierGroup = modifierGroup.id
    ? ownerNode.childModifierGroups?.[modifierGroup.id]
    : undefined;
  const selectedItems = Object.values(cartModifierGroup?.selectedItems || {});

  return {
    modifierGroup,
    selectedItems,
    hasExistingSelection: selectedItems.length > 0,
  };
};

const evaluateDefaultRuleForComboTarget = (
  targetName: string,
  state: ComboTargetState,
  defaultSizeRules: IBusinessRule[],
  options: {
    selectedContext?: {
      modifierGroupName: string;
      optionName: string;
    };
    selectedChildItems: Array<{ name: string; modifierGroupName: string }>;
  }
): DefaultSizeEvaluation => {
  const { selectedContext, selectedChildItems } = options;

  const isContextTarget =
    !!selectedContext &&
    normalizeName(selectedContext.modifierGroupName) ===
      normalizeName(targetName);

  const normalizedSelectedOptionName = selectedContext
    ? normalizeName(selectedContext.optionName)
    : null;

  const defaultRule = isContextTarget
    ? undefined
    : findDefaultRuleForTarget(defaultSizeRules, {
        targetName,
        hasExistingSelection: state.hasExistingSelection,
        normalizedSelectedOptionName,
        selectedChildItems,
        contextModifierGroupName: selectedContext?.modifierGroupName || null,
      });

  const enforceDefaultOptions = defaultRule
    ? [...defaultRule.then.target_options]
    : [];

  return {
    defaultRule,
    enforceDefaultOptions,
  };
};

type SizeCandidate = { name: string; [k: string]: any };

/**
 * Finds the menu item whose name best matches the given base size: the bare
 * size ("small"), then a size-first name ("small drink"), then any name
 * containing it ("extra small").
 *
 * One size group can hold a size for several kinds of drink — Galardi's
 * drink_size has "small" and "small shake", "regular drink" and "regular
 * shake" — and not-sold rules keep only one of each pair for the chosen drink.
 * So the best-ranked option that `isSold` accepts wins; only when none is sold
 * is the best-ranked one returned, for the caller's unavailable path (default
 * size rule) to handle (HITL-1698).
 */
const findMenuItemBySize = <T extends SizeCandidate>(
  candidateItems: T[],
  normalizedBaseSize: string,
  isSold: (item: T) => boolean = () => true
): T | undefined => {
  const exact: T[] = [];
  const sizeFirst: T[] = [];
  const containing: T[] = [];
  candidateItems.forEach((item) => {
    const name = normalizeName(item.name);
    if (name === normalizedBaseSize) exact.push(item);
    else if (name.startsWith(`${normalizedBaseSize} `)) sizeFirst.push(item);
    else if (name.includes(normalizedBaseSize)) containing.push(item);
  });
  const ranked = [...exact, ...sizeFirst, ...containing];
  return ranked.find(isSold) ?? ranked[0];
};

/** Whether a size option is unavailable on this cart item as it stands. */
const isSizeOptionUnavailable = (
  cartItem: CartItem,
  unavailableItemsByName: UnavailableItemsByName,
  item: SizeCandidate,
  modifierGroupName: string
): boolean =>
  item.available === false ||
  isBMModifierAnUnavailableConstraint(
    cartItem,
    unavailableItemsByName,
    getParentModifier(item.pathToItem || ''),
    item.pathToItem || '',
    modifierGroupName,
    item.name || item.itemId
  );

/**
 * Shared preamble for combo-size propagation paths: finds matching rules for
 * the given combo size option and extracts the base size token.
 * Returns null when no rules match or the base size can't be derived.
 */
const resolveComboSizeRulesAndBaseSize = (
  cartItemName: string,
  comboSizeName: string,
  comboSizeTransformationRules: IBusinessRule[]
): { matchingRules: IBusinessRule[]; baseSize: string } | null => {
  const matchingRules = findMatchingComboSizeTransformationRules(
    cartItemName,
    comboSizeName,
    comboSizeTransformationRules
  );

  if (matchingRules.length === 0) {
    return null;
  }

  const baseSize =
    (extractBaseSize(comboSizeName) ?? comboSizeName.trim()) || null;

  if (!baseSize) {
    return null;
  }

  return { matchingRules, baseSize };
};

const resolveComboSizeTransformationTargets = (
  cartItem: CartItem,
  baseSize: string,
  transformationRule: IBusinessRule,
  defaultSizeRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName,
  selectedContext?: {
    modifierGroupName: string;
    optionName: string;
  }
): Array<{ modifierGroupName: string; optionName: string }> => {
  const normalizedBaseSize = normalizeName(baseSize);
  const targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }> = [];

  /**
   * Collect ALL currently selected items across ALL modifier groups in the cart item.
   * This snapshot is used to match default rules based on what's already selected
   * in OTHER modifier groups (not just the one that triggered this evaluation).
   *
   * Example: If cart has:
   *   - Main Item: "burger" (selected)
   *   - Drinks: "medium drink" (selected)
   *   - Sides: (empty)
   * Then selectedChildItems = [
   *   { name: "burger", modifierGroupName: "Main Item" },
   *   { name: "medium drink", modifierGroupName: "Drinks" }
   * ]
   */
  const selectedChildItems = Object.values(
    cartItem.childModifierGroups || {}
  ).flatMap((group) =>
    Object.values(group.selectedItems || {}).map((item) => ({
      name: item.name,
      modifierGroupName: group.name,
    }))
  );

  // Single walk to collect all nested modifier groups; each target lookup is O(1)
  const nestedGroups = collectNestedModifierGroups(cartItem);

  transformationRule.then.target.forEach((targetName) => {
    const state = buildComboTargetState(cartItem, targetName, nestedGroups);

    if (!state) {
      return;
    }

    const candidateItems = Object.values(state.modifierGroup.menuItems || {});

    if (!candidateItems.length) {
      return;
    }

    const matchingItem = findMenuItemBySize(
      candidateItems,
      normalizedBaseSize,
      (item) =>
        !isSizeOptionUnavailable(
          cartItem,
          unavailableItemsByName,
          item,
          state.modifierGroup.name
        )
    );

    if (!matchingItem) {
      return;
    }

    const alreadySelected = state.selectedItems.some(
      (item) => normalizeName(item.name) === normalizeName(matchingItem.name)
    );

    // Check if item is unavailable via constraints or menu item availability.
    // Checked before `alreadySelected`: a size picked earlier can stop being
    // sold once a linked group's pick changes (Medium after Coke -> Tea), and
    // then it needs the default size rule, not to be left in place (HITL-1698).
    const isUnavailable = isSizeOptionUnavailable(
      cartItem,
      unavailableItemsByName,
      matchingItem,
      state.modifierGroup.name
    );

    if (isUnavailable) {
      const defaultEvaluation = evaluateDefaultRuleForComboTarget(
        targetName,
        state,
        defaultSizeRules,
        {
          selectedContext,
          selectedChildItems,
        }
      );

      if (defaultEvaluation.enforceDefaultOptions.length > 0) {
        defaultEvaluation.enforceDefaultOptions.forEach((optionName) => {
          const alreadyQueued = targetModifierGroups.some(
            (queued) =>
              queued.modifierGroupName === targetName &&
              queued.optionName === optionName
          );

          if (!alreadyQueued) {
            targetModifierGroups.push({
              modifierGroupName: targetName,
              optionName,
            });
          }
        });
      }
      return;
    }

    if (alreadySelected) {
      return;
    }

    targetModifierGroups.push({
      modifierGroupName: targetName,
      optionName: matchingItem.name,
    });
  });

  return targetModifierGroups;
};

/**
 * Applies combo size transformation rules to determine target modifier groups.
 * Returns replacement info if combo size transformation is applicable, null otherwise.
 */
const applyComboSizeRules = (
  modifierGroupName: string,
  selectedMenuItemName: string,
  cartItem: CartItem,
  comboSizeTransformationRules: IBusinessRule[],
  comboSizeOptions: Set<string>,
  defaultSizeRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName
): OptionReplacementInfo | null => {
  const normalizedSelectedOption = normalizeName(selectedMenuItemName);
  const isSelectedOptionComboSize =
    comboSizeOptions.has(normalizedSelectedOption) ||
    comboSizeTransformationRules.some((rule) => rule.if.options.includes('*'));

  // Get the effective combo size name
  // If the selected option is a combo size, use it directly; otherwise, don't use combo size transformation values
  const effectiveComboSizeName = isSelectedOptionComboSize
    ? selectedMenuItemName
    : false;

  if (!effectiveComboSizeName) {
    return null;
  }

  const resolved = resolveComboSizeRulesAndBaseSize(
    cartItem.name,
    effectiveComboSizeName,
    comboSizeTransformationRules
  );

  if (!resolved) {
    return null;
  }

  const { matchingRules, baseSize } = resolved;

  for (const comboSizeTransformationRule of matchingRules) {
    const targetModifierGroups = resolveComboSizeTransformationTargets(
      cartItem,
      baseSize,
      comboSizeTransformationRule,
      defaultSizeRules,
      unavailableItemsByName,
      isSelectedOptionComboSize
        ? undefined
        : {
            modifierGroupName,
            optionName: selectedMenuItemName,
          }
    );

    if (targetModifierGroups.length > 0) {
      logger.debug(
        `Combo size transformation applied for "${selectedMenuItemName}" (combo size "${effectiveComboSizeName}") using rule "${comboSizeTransformationRule.name}" targeting ${targetModifierGroups.length} modifier groups`
      );
      return {
        shouldApplyReplacement: true,
        targetModifierGroups,
      };
    }
  }

  return null;
};

/**
 * Whether a default size rule names the just-selected item itself (e.g.
 * "orange juice → regular"). Such a default takes precedence over generic
 * combo size propagation. Checks if.options too — a rule for "orange juice +
 * no ice" shouldn't count when "no ice" isn't selected.
 */
const hasItemSpecificDefaultRule = (
  selectedMenuItemName: string,
  defaultSizeRules: IBusinessRule[]
): boolean =>
  defaultSizeRules.some((rule) => {
    if (
      rule.items_to_apply.length === 0 ||
      hasWildcardItem(rule.items_to_apply)
    ) {
      return false;
    }
    if (!matchesRuleItems(rule.items_to_apply, [selectedMenuItemName])) {
      return false;
    }
    const normalizedSelected = normalizeName(selectedMenuItemName);
    return rule.if.options.some(
      (opt) => opt === '*' || normalizeName(opt) === normalizedSelected
    );
  });

/**
 * Applies default size rules to determine target modifier groups.
 * Only applies to targets that have no existing selection or have invalid/unavailable selections.
 * Returns replacement info if default size rule is applicable, null otherwise.
 */
const applyDefaultSizeRules = (
  selectedMenuItemName: string,
  cartItem: CartItem,
  defaultSizeRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName,
  selectedContext?: {
    modifierGroupName: string;
    optionName: string;
  }
): OptionReplacementInfo | null => {
  const matchingDefaultRule = findMatchingDefaultSizeRule(
    cartItem.name,
    selectedMenuItemName,
    defaultSizeRules
  );

  if (!matchingDefaultRule) {
    return null;
  }

  const targets = matchingDefaultRule.then.target;

  const targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }> = [];

  if (matchingDefaultRule.then.target_options.length > 0) {
    // For each target, if it exists in the cart item and has no selection or invalid selection,
    // apply all target_options to it
    targets.forEach((target) => {
      const state = buildComboTargetState(cartItem, target);
      if (!state) {
        return;
      }

      // Check if there's no selection OR if existing selections are unavailable
      // Also check if existing selections become invalid when combined with the newly selected item
      const hasInvalidSelection = hasInvalidSelectionForTarget(
        state,
        cartItem,
        target,
        unavailableItemsByName,
        selectedContext
      );

      if (hasInvalidSelection) {
        matchingDefaultRule.then.target_options.forEach((targetOption) => {
          targetModifierGroups.push({
            modifierGroupName: target,
            optionName: targetOption,
          });
        });
      }
    });
  }

  if (targetModifierGroups.length > 0) {
    logger.debug(
      `Default size rule applied: ${selectedMenuItemName} -> applying to ${targetModifierGroups.length} modifier groups`
    );
    return {
      shouldApplyReplacement: true,
      targetModifierGroups,
    };
  }

  return null;
};

/**
 * Names of the size groups whose pick depends on `modifierGroupName` on this
 * cart item. Two kinds of rule tie a group to a size group:
 *
 * - a brand-menu unavailability rule naming both, e.g. "Drink Size" for
 *   "Drink" under `{Drink: [Tea], Drink Size: [Medium, Large]}`;
 * - a default size rule targeting the size group that lists options of this
 *   group, e.g. "drink_size" for "drink_choice" when "select default regular
 *   drink size" lists the shakes.
 */
const getLinkedSizeGroupNames = (
  cartItem: CartItem,
  modifierGroupName: string,
  defaultSizeRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName
): Set<string> => {
  const linked = new Set<string>();
  [
    ...(unavailableItemsByName[cartItem.name] || []),
    ...(unavailableItemsByName[ALL_ENTRIES] || []),
  ].forEach(({ constraints }) => {
    if (!constraints?.[modifierGroupName]) return;
    Object.keys(constraints).forEach((name) => {
      if (name !== modifierGroupName) linked.add(name);
    });
  });

  const optionNames = Object.values(cartItem.modifierGroups || {})
    .filter((group) => group.name === modifierGroupName)
    .flatMap((group) =>
      Object.values(group.menuItems || {}).map((item) => item.name)
    );
  if (optionNames.length) {
    defaultSizeRules.forEach((rule) => {
      if (hasWildcardItem(rule.items_to_apply)) return;
      if (matchesRuleItems(rule.items_to_apply, optionNames)) {
        rule.then.target.forEach((target) => {
          if (target !== modifierGroupName) linked.add(target);
        });
      }
    });
  }
  return linked;
};

/**
 * Linked-sibling combo size propagation (HITL-1698): triggered when a
 * non-combo-size item is selected in a group that a rule links to a
 * top-level combo size target (see getLinkedSizeGroupNames), e.g. Drink next
 * to Drink Size.
 *
 * Deferred propagation only covers a size group nested under the clicked
 * item. When the size is a sibling, switching the drink changes which sizes
 * are sold without re-running the combo size rule, so Large Combo with
 * Coke -> Shake -> Coke kept the shake's regular shake. This re-applies the
 * combo size to a linked target that the new pick left empty or holding a
 * size it doesn't sell; when the combo size is not sold either, the default
 * size rule for the target applies instead.
 *
 * A size that is still sold with the new pick is left alone: it may be one
 * the agent chose by hand (review of !1717). So a fallback size the next
 * drink also sells stays — Tea's Regular survives Tea -> Coke.
 *
 * The combo size wins whenever the new drink has a sold option for it —
 * Small Combo with a shake is "small shake" — and the drink's default size
 * rule only fills in when it has none (Large Combo with a shake is "regular
 * shake"). Unlinked targets (Side Size when the drink changes) are left alone.
 */
const applyLinkedSiblingComboSizePropagation = (
  cartItem: CartItem,
  modifierGroupName: string,
  selectedMenuItemName: string,
  comboSizeTransformationRules: IBusinessRule[],
  comboSizeOptions: Set<string>,
  defaultSizeRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName
): OptionReplacementInfo | null => {
  // A combo size pick is applyComboSizeRules' to propagate. Re-propagating
  // here would read the size from the cart, which can still hold the previous
  // one (HITL-1357).
  if (comboSizeOptions.has(normalizeName(selectedMenuItemName))) {
    return null;
  }

  const linkedGroupNames = getLinkedSizeGroupNames(
    cartItem,
    modifierGroupName,
    defaultSizeRules,
    unavailableItemsByName
  );

  if (linkedGroupNames.size === 0) {
    return null;
  }

  const selectedComboSize = getSelectedComboSize(cartItem, comboSizeOptions);

  if (!selectedComboSize) {
    return null;
  }

  const resolved = resolveComboSizeRulesAndBaseSize(
    cartItem.name,
    selectedComboSize,
    comboSizeTransformationRules
  );

  if (!resolved) {
    return null;
  }

  const { matchingRules, baseSize } = resolved;

  for (const rule of matchingRules) {
    // Only a size the new pick left empty or no longer sold is re-applied. A
    // size that is still valid stays: it may be one the agent chose by hand.
    const linkedTargets = rule.then.target.filter((target) => {
      if (!linkedGroupNames.has(target)) return false;
      const state = buildComboTargetState(cartItem, target);
      return (
        !!state &&
        hasInvalidSelectionForTarget(
          state,
          cartItem,
          target,
          unavailableItemsByName
        )
      );
    });

    if (linkedTargets.length === 0) {
      continue;
    }

    const targetModifierGroups = resolveComboSizeTransformationTargets(
      cartItem,
      baseSize,
      { ...rule, then: { ...rule.then, target: linkedTargets } },
      defaultSizeRules,
      unavailableItemsByName,
      { modifierGroupName, optionName: selectedMenuItemName }
    );

    if (targetModifierGroups.length > 0) {
      logger.debug(
        `Linked-sibling combo size propagation applied for "${selectedMenuItemName}" in "${modifierGroupName}" (combo size "${selectedComboSize}") using rule "${rule.name}" targeting ${targetModifierGroups.length} modifier groups`
      );
      return {
        shouldApplyReplacement: true,
        targetModifierGroups,
      };
    }
  }

  // The combo size is not sold with the new pick and no default keyed on the
  // pick covers it. A default keyed on the combo size itself ("Medium Combo"
  // → Regular) is what re-picking the combo size would have applied, so apply
  // it now to the linked targets whose selection is no longer valid.
  const comboSizeDefault = applyDefaultSizeRules(
    selectedComboSize,
    cartItem,
    defaultSizeRules,
    unavailableItemsByName
  );
  const linkedDefaults =
    comboSizeDefault?.targetModifierGroups.filter(({ modifierGroupName }) =>
      linkedGroupNames.has(modifierGroupName)
    ) || [];

  if (linkedDefaults.length > 0) {
    logger.debug(
      `Linked-sibling default size applied for "${selectedMenuItemName}" in "${modifierGroupName}" (combo size "${selectedComboSize}") targeting ${linkedDefaults.length} modifier groups`
    );
    return {
      shouldApplyReplacement: true,
      targetModifierGroups: linkedDefaults,
    };
  }

  return null;
};

/**
 * Deferred combo size propagation: triggered when a non-combo-size item is
 * selected (e.g. "Coke" in the drink group). Checks whether a combo size is
 * already selected elsewhere in the cart, and if the just-selected item
 * exposed new nested modifier groups that are targets of combo size rules,
 * propagates the size to them.
 *
 * Skipped when a default size rule specifically matches the just-selected item,
 * since item-specific defaults (e.g. "orange juice → regular") should take
 * precedence over generic combo size propagation.
 */
const applyDeferredComboSizePropagation = (
  cartItem: CartItem,
  modifierGroupName: string,
  selectedMenuItemName: string,
  comboSizeTransformationRules: IBusinessRule[],
  comboSizeOptions: Set<string>,
  defaultSizeRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName
): OptionReplacementInfo | null => {
  const clickedItemGroups = getSelectedItemNestedGroups(
    cartItem,
    modifierGroupName,
    selectedMenuItemName
  );

  if (!clickedItemGroups) {
    return null;
  }

  if (hasItemSpecificDefaultRule(selectedMenuItemName, defaultSizeRules)) {
    return null;
  }

  const selectedComboSize = getSelectedComboSize(cartItem, comboSizeOptions);

  if (!selectedComboSize) {
    return null;
  }

  const resolved = resolveComboSizeRulesAndBaseSize(
    cartItem.name,
    selectedComboSize,
    comboSizeTransformationRules
  );

  if (!resolved) {
    return null;
  }

  const { matchingRules, baseSize } = resolved;
  const normalizedBaseSize = normalizeName(baseSize);
  const targetModifierGroups: Array<{
    modifierGroupName: string;
    optionName: string;
  }> = [];

  const selectedChildItems = Object.values(
    cartItem.childModifierGroups || {}
  ).flatMap((group) =>
    Object.values(group.selectedItems || {}).map((item) => ({
      name: item.name,
      modifierGroupName: group.name,
    }))
  );

  for (const rule of matchingRules) {
    for (const targetName of rule.then.target) {
      const entry = clickedItemGroups.get(targetName);
      if (!entry) {
        continue;
      }

      const candidateItems = Object.values(entry.modifierGroup.menuItems || {});
      const isSold = (item: SizeCandidate) =>
        !isSizeOptionUnavailable(
          cartItem,
          unavailableItemsByName,
          item,
          entry.modifierGroup.name
        );
      const matchingItem = findMenuItemBySize(
        candidateItems,
        normalizedBaseSize,
        isSold
      );

      if (!matchingItem) {
        continue;
      }

      // Skip if the target is unavailable — fall back to default size rule
      const isUnavailable = !isSold(matchingItem);

      if (isUnavailable) {
        const state: ComboTargetState = {
          modifierGroup: entry.modifierGroup,
          selectedItems: [],
          hasExistingSelection: false,
        };
        const defaultEvaluation = evaluateDefaultRuleForComboTarget(
          targetName,
          state,
          defaultSizeRules,
          { selectedChildItems }
        );
        defaultEvaluation.enforceDefaultOptions.forEach((optionName) => {
          targetModifierGroups.push({
            modifierGroupName: targetName,
            optionName,
          });
        });
        continue;
      }

      targetModifierGroups.push({
        modifierGroupName: targetName,
        optionName: matchingItem.name,
      });
    }

    if (targetModifierGroups.length > 0) {
      logger.debug(
        `Deferred combo size propagation applied for already-selected combo size "${selectedComboSize}" using rule "${rule.name}" targeting ${targetModifierGroups.length} modifier groups`
      );
      return {
        shouldApplyReplacement: true,
        targetModifierGroups,
      };
    }
  }

  return null;
};

/**
 * Analyze if option replacement should be applied based on modifier selection
 * Uses business rules from the menu JSON exclusively (no config fallback)
 */
export const analyzeOptionReplacement = (
  modifierGroupName: string,
  selectedMenuItemName: string,
  cartItem: CartItem,
  businessRules: IBusinessRule[],
  unavailableItemsByName: UnavailableItemsByName
): OptionReplacementInfo => {
  if (!businessRules || businessRules.length === 0) {
    return { shouldApplyReplacement: false, targetModifierGroups: [] };
  }

  const defaultSizeRules = businessRules.filter(
    (rule) =>
      rule.category === BusinessRuleCategory.DefaultSizeSelection &&
      rule.then.operation === BusinessRuleOption.SelectDefaultSize
  );

  // Get combo size transformation rules to find selected combo size
  // Check combo size transformation rules FIRST, before default size rules
  const comboSizeTransformationRules = businessRules.filter(
    (rule) =>
      rule.category === BusinessRuleCategory.ComboSizeTransformation &&
      rule.then.operation === BusinessRuleOption.TransformComboSize
  );

  // Build once, reuse across applyComboSizeRules and deferred propagation
  const comboSizeOptions = buildComboSizeOptionsSet(
    comboSizeTransformationRules
  );

  // Try combo size transformation rules first
  const comboResult = applyComboSizeRules(
    modifierGroupName,
    selectedMenuItemName,
    cartItem,
    comboSizeTransformationRules,
    comboSizeOptions,
    defaultSizeRules,
    unavailableItemsByName
  );

  if (comboResult) {
    return comboResult;
  }

  // Deferred propagation: when a non-combo-size item is selected (e.g. "Coke"),
  // check if a combo size is already selected. If so, propagate it to the
  // just-selected item's nested modifier groups that are rule targets.
  if (comboSizeTransformationRules.length > 0) {
    const deferredResult = applyDeferredComboSizePropagation(
      cartItem,
      modifierGroupName,
      selectedMenuItemName,
      comboSizeTransformationRules,
      comboSizeOptions,
      defaultSizeRules,
      unavailableItemsByName
    );

    if (deferredResult) {
      return deferredResult;
    }

    // Same, for a size group that sits next to the clicked group rather than
    // under the clicked item (HITL-1698).
    const linkedSiblingResult = applyLinkedSiblingComboSizePropagation(
      cartItem,
      modifierGroupName,
      selectedMenuItemName,
      comboSizeTransformationRules,
      comboSizeOptions,
      defaultSizeRules,
      unavailableItemsByName
    );

    if (linkedSiblingResult) {
      return linkedSiblingResult;
    }
  }

  // Apply default size rules only if combo size transformation rules produced no targets
  const defaultResult = applyDefaultSizeRules(
    selectedMenuItemName,
    cartItem,
    defaultSizeRules,
    unavailableItemsByName,
    {
      modifierGroupName,
      optionName: selectedMenuItemName,
    }
  );

  if (defaultResult) {
    return defaultResult;
  }

  return { shouldApplyReplacement: false, targetModifierGroups: [] };
};
